import { useBackendApi, useObjectDatesMapper, EventTask, useTimezoneService } from 'nekst-api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface AttendeeDto {
  email: string;
  fullName: string | null;
}

export interface EventPreviewResponse {
  title: string;
  description: string;
  location: string | null;
  startTime: Date | null;
  endTime: Date | null;
  isAllDayEvent: boolean;
  attendees: AttendeeDto[];
  organizer: AttendeeDto | null;
}

interface EventPreviewRequest {
  name: string;
  description?: string;
  location?: string;
  dueDate?: {
    date: string;
    time: string | null;
    endTime: string | null;
    timezone: string;
  };
  inviteRecipients: Array<{ personId: number }>;
  assignments: Array<{ personId: number }>;
  _version: number;
}

export function useEventPreviewService() {
  const nekstApi = useBackendApi();
  const timezoneService = useTimezoneService();

  const { mapSingle } = useObjectDatesMapper<EventPreviewResponse>(
    [],
    ['startTime', 'endTime']
  );

  const buildRequestData = (eventData: Partial<EventTask>): EventPreviewRequest => {
    const tz = eventData.dueDate?.timezone || timezoneService.getTimezone();

    const formatDateInTimezone = (date: Date | string | null | undefined): string | null => {
      if (!date) return null;
      if (typeof date === 'string') return date;
      // Format the Date as local time string in the specified timezone
      return dayjs(date).tz(tz).format('YYYY-MM-DD HH:mm:ss');
    };

    return {
      name: eventData.name || '',
      description: eventData.description,
      location: eventData.location,
      dueDate: eventData.dueDate ? {
        date: eventData.dueDate.date instanceof Date
          ? dayjs(eventData.dueDate.date).format('YYYY-MM-DD')
          : eventData.dueDate.date,
        time: formatDateInTimezone(eventData.dueDate.time),
        endTime: formatDateInTimezone(eventData.dueDate.endTime),
        timezone: tz,
      } : undefined,
      inviteRecipients: (eventData.inviteRecipients || []).map(recipient => ({
        personId: recipient.personId,
      })),
      assignments: (eventData.assignments || [])
        .filter(assignment => assignment.personId !== undefined)
        .map(assignment => ({
          personId: assignment.personId!,
        })),
      _version: eventData._version || 0,
    };
  };

  const getEventPreview = async (
    taskId: number,
    eventData: EventTask
  ): Promise<EventPreviewResponse> => {
    const requestData = buildRequestData(eventData);
    const response = await nekstApi.post(`/tasks/${taskId}/eventpreview`, requestData);
    return mapSingle(response);
  };

  const getEventPreviewForProject = async (
    projectId: number,
    eventData: Partial<EventTask>
  ): Promise<EventPreviewResponse> => {
    const requestData = buildRequestData(eventData);
    const response = await nekstApi.post(`/projects/${projectId}/eventpreview`, requestData);
    return mapSingle(response);
  };

  return {
    getEventPreview,
    getEventPreviewForProject,
  };
}
